import React, { useState, useRef, Children } from 'react';
import { motion } from 'framer-motion';
import { BookOpenIcon, HeadphonesIcon, SparklesIcon, CheckIcon, PlayIcon, PauseIcon, ClockIcon } from 'lucide-react';
import { GlowButton } from './GlowButton';
const AUDIO_PREVIEW_URL = 'https://github.com/jvzng/webpage_chatbot_closer/raw/980fc46fbc1f0174fb4817a62896dc782237fcd9/ElevenLabs_2026-02-19T20_15_33_Declan%20Sage%20-%20Wise%2C%20Deliberate%2C%20Captivating_pvc_sp76_s50_sb50_se48_b_m2.mp3';
function AudioPreview() {
  const audioRef = useRef<HTMLAudioElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const togglePlay = () => {
    if (!audioRef.current) return;
    if (isPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.play();
    }
    setIsPlaying(!isPlaying);
  };
  const handleTimeUpdate = () => {
    if (!audioRef.current) return;
    const current = audioRef.current.currentTime;
    const dur = audioRef.current.duration;
    setCurrentTime(current);
    if (dur) setProgress(current / dur * 100);
  };
  const handleLoadedMetadata = () => {
    if (!audioRef.current) return;
    setDuration(audioRef.current.duration);
  };
  const handleSeek = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!audioRef.current || !duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const pct = x / rect.width;
    audioRef.current.currentTime = pct * duration;
  };
  const handleEnded = () => {
    setIsPlaying(false);
    setProgress(0);
    setCurrentTime(0);
  };
  const formatTime = (t: number) => {
    const mins = Math.floor(t / 60);
    const secs = Math.floor(t % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };
  return <div className="rounded-xl bg-white/[0.04] border border-purple-electric/15 p-4 mb-6" data-id="element-158">
      <audio ref={audioRef} src={AUDIO_PREVIEW_URL} onTimeUpdate={handleTimeUpdate} onLoadedMetadata={handleLoadedMetadata} onEnded={handleEnded} preload="metadata" data-id="element-159" />
      <p className="font-sans text-[11px] text-purple-electric-light/60 tracking-wider uppercase mb-3" data-id="element-160">
        ▸ Escucha un fragmento
      </p>
      <div className="flex items-center gap-3" data-id="element-161">
        <button onClick={togglePlay} className="w-10 h-10 rounded-full bg-purple-electric/20 border border-purple-electric/30 flex items-center justify-center flex-shrink-0 hover:bg-purple-electric/30 transition-colors duration-300" data-id="element-162">
          {isPlaying ? <PauseIcon className="w-4 h-4 text-purple-electric-light" data-id="element-163" /> : <PlayIcon className="w-4 h-4 text-purple-electric-light ml-0.5" data-id="element-164" />}
        </button>
        <div className="flex-1 min-w-0" data-id="element-165">
          <div className="w-full h-1.5 bg-white/[0.06] rounded-full cursor-pointer group" onClick={handleSeek} data-id="element-166">
            <div className="h-full bg-gradient-to-r from-purple-electric to-purple-electric-light rounded-full transition-all duration-100 relative" style={{
            width: `${progress}%`
          }} data-id="element-167">
              <div className="absolute right-0 top-1/2 -translate-y-1/2 w-2.5 h-2.5 bg-purple-electric-light rounded-full opacity-0 group-hover:opacity-100 transition-opacity shadow-glow-purple" data-id="element-168" />
            </div>
          </div>
          <div className="flex justify-between mt-1.5" data-id="element-169">
            <span className="font-sans text-[10px] text-white/25" data-id="element-170">
              {formatTime(currentTime)}
            </span>
            <span className="font-sans text-[10px] text-white/25" data-id="element-171">
              {duration ? formatTime(duration) : '--:--'}
            </span>
          </div>
        </div>
      </div>
    </div>;
}
const containerVariants = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.2
    }
  }
};
const cardVariants = {
  hidden: {
    opacity: 0,
    y: 30
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.8,
      ease: [0.22, 1, 0.36, 1]
    }
  }
};
function FeatureItem({
  children
}: {
  children: React.ReactNode;
}) {
  return <li className="flex items-start gap-3" data-id="element-172">
      <CheckIcon className="w-4 h-4 text-gold mt-0.5 flex-shrink-0" data-id="element-173" />
      <span className="font-sans text-white/50 text-sm leading-relaxed" data-id="element-174">
        {children}
      </span>
    </li>;
}
export function ProductSection() {
  return <section id="producto" className="relative w-full py-24 sm:py-32 overflow-hidden" data-id="element-175">
      <div className="absolute top-1/2 left-0 w-[400px] h-[400px] bg-flame/[0.04] rounded-full blur-[120px] pointer-events-none" data-id="element-176" />
      <div className="absolute bottom-0 right-0 w-[350px] h-[350px] bg-purple-electric/[0.04] rounded-full blur-[100px] pointer-events-none" data-id="element-177" />

      <div className="relative max-w-6xl mx-auto px-6" data-id="element-178">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-60px'
      }} transition={{
        duration: 0.8,
        ease: [0.22, 1, 0.36, 1]
      }} className="text-center mb-16" data-id="element-179">
          <p className="font-sans text-sm text-gold tracking-[0.2em] uppercase mb-4" data-id="element-180">
            Un pack completo
          </p>
          <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-4" data-id="element-181">
            Ebook + Audiobook incluidos
          </h2>
          <p className="font-sans text-white/40 text-lg max-w-2xl mx-auto" data-id="element-182">
            No tienes que elegir. Al adquirir el libro recibes ambos formatos:
            léelo a tu ritmo y escúchalo mientras meditas.
          </p>
        </motion.div>

        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-60px'
      }} className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto" data-id="element-183">
          {/* Ebook Card */}
          <motion.div variants={cardVariants} className="group relative" data-id="element-184">
            <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-flame/15 to-gold/10 opacity-0 group-hover:opacity-100 transition-opacity duration-700 blur-xl" data-id="element-185" />
            <div className="relative rounded-2xl bg-white/[0.03] backdrop-blur-xl border border-white/[0.08] p-8 sm:p-10 h-full overflow-hidden" data-id="element-186">
              <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-flame/50 to-transparent" data-id="element-187" />
              <div className="absolute top-4 right-4" data-id="element-188">
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-gold/10 border border-gold/20 text-gold-light text-xs font-sans font-medium" data-id="element-189">
                  <CheckIcon className="w-3 h-3" data-id="element-190" />
                  Incluido
                </span>
              </div>
              <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-flame/20 to-flame/5 border border-flame/20 flex items-center justify-center mb-6" data-id="element-191">
                <BookOpenIcon className="w-8 h-8 text-flame-light" data-id="element-192" />
              </div>
              <h3 className="font-serif text-2xl sm:text-3xl font-bold text-white mb-2" data-id="element-193">
                Ebook
              </h3>
              <p className="font-sans text-flame-light/70 text-sm tracking-wide uppercase mb-6" data-id="element-194">
                Formato digital
              </p>
              <ul className="space-y-3" data-id="element-195">
                <FeatureItem data-id="element-196">10 capítulos completos</FeatureItem>
                <FeatureItem data-id="element-197">Lectura en cualquier dispositivo</FeatureItem>
                <FeatureItem data-id="element-198">Descarga inmediata</FeatureItem>
                <FeatureItem data-id="element-199">Formato PDF + EPUB</FeatureItem>
              </ul>
            </div>
          </motion.div>

          {/* Audiobook Card — NO BUTTON */}
          <motion.div variants={cardVariants} className="group relative" data-id="element-200">
            <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-purple-electric/15 to-astral/10 opacity-0 group-hover:opacity-100 transition-opacity duration-700 blur-xl" data-id="element-201" />
            <div className="relative rounded-2xl bg-white/[0.03] backdrop-blur-xl border border-white/[0.08] p-8 sm:p-10 h-full overflow-hidden" data-id="element-202">
              <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-purple-electric/50 to-transparent" data-id="element-203" />
              <div className="absolute top-4 right-4" data-id="element-204">
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-purple-electric/15 border border-purple-electric/25 text-purple-electric-light text-xs font-sans font-medium" data-id="element-205">
                  <CheckIcon className="w-3 h-3" data-id="element-206" />
                  Incluido
                </span>
              </div>
              <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-purple-electric/20 to-purple-electric/5 border border-purple-electric/20 flex items-center justify-center mb-6" data-id="element-207">
                <HeadphonesIcon className="w-8 h-8 text-purple-electric-light" data-id="element-208" />
              </div>
              <h3 className="font-serif text-2xl sm:text-3xl font-bold text-white mb-2" data-id="element-209">
                Audiobook
              </h3>
              <p className="font-sans text-purple-electric-light/70 text-sm tracking-wide uppercase mb-6" data-id="element-210">
                Narración inmersiva
              </p>
              <AudioPreview data-id="element-211" />
              <ul className="space-y-3 mb-4" data-id="element-212">
                <FeatureItem data-id="element-213">Narración profesional completa</FeatureItem>
                <FeatureItem data-id="element-214">Ideal para meditación y viajes</FeatureItem>
                <FeatureItem data-id="element-215">Escucha offline</FeatureItem>
                <FeatureItem data-id="element-216">Formato MP3 de alta calidad</FeatureItem>
              </ul>
              <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-white/[0.03] border border-white/[0.06] w-fit" data-id="element-217">
                <ClockIcon className="w-3.5 h-3.5 text-purple-electric-light/60" data-id="element-218" />
                <span className="font-sans text-xs text-white/40" data-id="element-219">
                  Duración total:{' '}
                  <span className="text-purple-electric-light/80 font-medium" data-id="element-220">
                    2 horas
                  </span>
                </span>
              </div>
            </div>
          </motion.div>
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        y: 15
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.8,
        delay: 0.4,
        ease: [0.22, 1, 0.36, 1]
      }} className="flex flex-col items-center mt-14 gap-5" data-id="element-221">
          <GlowButton href="#obtener-pack" variant="gold" data-id="element-222">
            Obtener Pack
          </GlowButton>
          <div className="inline-flex items-center gap-3 px-6 py-3 rounded-full bg-white/[0.03] backdrop-blur-sm border border-gold/15" data-id="element-223">
            <SparklesIcon className="w-4 h-4 text-gold" data-id="element-224" />
            <span className="font-sans text-white/50 text-sm" data-id="element-225">
              Ebook + Audiobook — todo en una sola compra
            </span>
          </div>
        </motion.div>
      </div>
    </section>;
}